import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, Clock, ExternalLink, MapPin } from 'lucide-react';
import {
  canViewTeamAttendance,
  getAttendanceRecords,
  getAttendanceSummary,
  type AttendanceDayRecord,
  type AttendanceLocation,
  type AttendanceSummaryStats,
} from '../api/attendance';
import { useAuth } from '../store/auth';
import { hasMapLink, locationLabel } from '@/lib/attendance-location';
import {
  ATTENDANCE_STATUS_LABELS,
  ATTENDANCE_STATUS_VARIANT,
  type AttendanceStatus,
} from '@hrmydeck/design-tokens/status';
import { APP_LOCALE, formatDate, formatHours, formatMinutes, formatTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import EmptyState from './ui/EmptyState';
import LoadingState from './ui/LoadingState';

type ViewTab = 'mine' | 'team';

function monthLabel(month: number, year: number) {
  return formatDate(`${year}-${String(month).padStart(2, '0')}-01`, {
    month: 'long',
    year: 'numeric',
  });
}

/** A calendar day collapsed to ONE row even when the employee clocked in and
 * out multiple times (multiple sessions): first clock-in → last clock-out,
 * summed hours, and the day's late flag (which belongs to the first session). */
type MergedDayRecord = AttendanceDayRecord & { sessionCount: number; sessionSpans: string[] };

function mergeDayRecords(sessions: AttendanceDayRecord[]): MergedDayRecord {
  const sorted = [...sessions].sort((a, b) =>
    (a.clockInTime ?? '').localeCompare(b.clockInTime ?? '')
  );
  const round2 = (n: number) => Math.round(n * 100) / 100;

  return {
    ...sorted[0],
    clockInTime: sorted[0].clockInTime,
    clockOutTime: sorted[sorted.length - 1].clockOutTime,
    workHours: round2(sorted.reduce((sum, r) => sum + (r.workHours ?? 0), 0)),
    totalHours: round2(sorted.reduce((sum, r) => sum + (r.totalHours ?? 0), 0)),
    requiresAttention: sorted.some((r) => r.requiresAttention),
    isLate: sorted[0].isLate,
    lateMinutes: sorted[0].lateMinutes,
    sessionCount: sorted.length,
    sessionSpans: sorted.map((r) =>
      `${r.clockInTime ? formatTime(r.clockInTime) : '—'}–${
        r.clockOutTime ? formatTime(r.clockOutTime) : 'open'
      }`
    ),
  };
}

function mergeByDate(records: AttendanceDayRecord[], keyByEmployee: boolean): MergedDayRecord[] {
  const groups = records.reduce<Record<string, AttendanceDayRecord[]>>((acc, record) => {
    const key = keyByEmployee ? `${record.employee?.id ?? 'unknown'}|${record.date}` : record.date;
    (acc[key] ??= []).push(record);
    return acc;
  }, {});

  return Object.values(groups)
    .map(mergeDayRecords)
    .sort((a, b) => b.date.localeCompare(a.date));
}

function startOfWeek(d: Date): Date {
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function weekLabel(monday: Date, sunday: Date): string {
  const dayMonth = (d: Date) =>
    d.toLocaleDateString(APP_LOCALE, { day: 'numeric', month: 'short' }).toUpperCase();
  const dayOnly = (d: Date) => d.toLocaleDateString(APP_LOCALE, { day: 'numeric' });

  if (monday.getMonth() === sunday.getMonth()) {
    return `${dayOnly(monday)} – ${dayMonth(sunday)}`;
  }
  return `${dayMonth(monday)} – ${dayMonth(sunday)}`;
}

type WeekGroup = { key: string; label: string; rows: MergedDayRecord[] };

function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function groupByWeek(rows: MergedDayRecord[]): WeekGroup[] {
  const groups = new Map<string, MergedDayRecord[]>();
  for (const row of rows) {
    const d = new Date(`${row.date}T12:00:00`);
    const monday = startOfWeek(d);
    const key = localDateKey(monday);
    const bucket = groups.get(key);
    if (bucket) bucket.push(row);
    else groups.set(key, [row]);
  }

  return Array.from(groups.entries())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, rows]) => {
      const monday = new Date(`${key}T12:00:00`);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { key, label: weekLabel(monday, sunday), rows };
    });
}

function LocationLine({
  label,
  location,
}: {
  label: string;
  location: AttendanceLocation | null | undefined;
}) {
  if (!location?.siteName && !hasMapLink(location)) return null;

  const text = locationLabel(location, label);

  if (hasMapLink(location)) {
    return (
      <a
        href={location.mapUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 inline-flex items-center gap-1 text-xs text-primary"
      >
        <MapPin className="h-3 w-3 shrink-0" />
        <span>
          {label}: {text}
        </span>
        <ExternalLink className="h-3 w-3 shrink-0 opacity-70" />
      </a>
    );
  }

  return (
    <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
      <MapPin className="h-3 w-3 shrink-0" />
      {label}: {text}
    </p>
  );
}

function DayRow({
  record,
  showEmployee,
}: {
  record: MergedDayRecord;
  showEmployee?: boolean;
}) {
  const dayDate = new Date(`${record.date}T12:00:00`);
  const isToday = record.date === localDateKey(new Date());
  const dayNumber = dayDate.toLocaleDateString(APP_LOCALE, { day: 'numeric' });
  const weekday = isToday
    ? 'TODAY'
    : dayDate.toLocaleDateString(APP_LOCALE, { weekday: 'short' }).toUpperCase();

  const subCaption =
    record.status === 'LEAVE'
      ? 'Approved leave'
      : !record.clockInTime
        ? 'No clock-in'
        : (record.sessionCount ?? 0) > 1 && record.sessionSpans
          ? `${record.sessionCount} sessions: ${record.sessionSpans.join(' · ')}`
          : record.requiresAttention
            ? 'Missing clock-out'
            : '';

  return (
    <div className="flex items-start justify-between gap-3 border-b border-border py-3 last:border-0">
      <div className="flex min-w-0 gap-3">
        <div className="w-11 shrink-0 text-center">
          <p className="text-lg font-semibold tabular-nums text-foreground">{dayNumber}</p>
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap">
            {weekday}
          </p>
        </div>
        <div className="min-w-0">
          {showEmployee && record.employee?.fullName && (
            <p className="text-sm font-medium text-primary">{record.employee.fullName}</p>
          )}
          <div className="flex items-center gap-1.5 text-sm font-medium tabular-nums text-foreground">
            <span>{record.clockInTime ? formatTime(record.clockInTime) : '--:--'}</span>
            <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span>{record.clockOutTime ? formatTime(record.clockOutTime) : '--:--'}</span>
          </div>
          {subCaption && <p className="mt-0.5 text-xs text-muted-foreground">{subCaption}</p>}
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {record.requiresAttention && <Badge variant="warning">Missed clock-out</Badge>}
            {record.isLate && (
              <Badge variant="destructive">
                Late{record.lateMinutes ? ` ${formatMinutes(record.lateMinutes)}` : ''}
              </Badge>
            )}
            {record.isEarlyDeparture && (
              <Badge variant="secondary">
                Undertime
                {record.earlyDepartureMinutes ? ` ${formatMinutes(record.earlyDepartureMinutes)}` : ''}
              </Badge>
            )}
            {record.clockOutTime && record.workHours > 0 && (
              <Badge variant="success">{formatHours(record.workHours)} worked</Badge>
            )}
          </div>
          <LocationLine label="Clock-in" location={record.clockInLocation} />
          <LocationLine label="Clock-out" location={record.clockOutLocation} />
        </div>
      </div>
      <Badge variant={ATTENDANCE_STATUS_VARIANT[record.status] ?? 'secondary'} className="shrink-0 whitespace-nowrap">
        {record.statusLabel ?? ATTENDANCE_STATUS_LABELS[record.status as AttendanceStatus] ?? record.status}
      </Badge>
    </div>
  );
}

export default function AttendanceHistory() {
  const { user } = useAuth();
  const canViewTeam = user ? canViewTeamAttendance(user.role) : false;

  const now = new Date();
  const [viewTab, setViewTab] = useState<ViewTab>('mine');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [stats, setStats] = useState<AttendanceSummaryStats | null>(null);
  const [records, setRecords] = useState<AttendanceDayRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    if (viewTab === 'mine') {
      const res = await getAttendanceSummary({ month, year });
      if (res.data?.data) {
        setStats(res.data.data.summary);
        setRecords(res.data.data.attendances);
      } else if (res.error) {
        setError(res.error);
        setStats(null);
        setRecords([]);
      }
    } else {
      setStats(null);
      const res = await getAttendanceRecords({ scope: 'team', month, year, per_page: 100 });
      if (res.data?.data) setRecords(res.data.data);
      else if (res.error) {
        setError(res.error);
        setRecords([]);
      }
    }

    setLoading(false);
  }, [month, year, viewTab]);

  useEffect(() => {
    load();
  }, [load]);

  const mergedRecords = useMemo(
    () => mergeByDate(records, viewTab === 'team'),
    [records, viewTab]
  );

  const breakdown = useMemo(() => {
    let present = 0;
    let late = 0;
    let wfh = 0;
    let outstation = 0;
    for (const row of mergedRecords) {
      if (!row.clockInTime) continue;
      if (row.isLate) late += 1;
      else if (row.clockInMode === 'REMOTE') wfh += 1;
      else if (row.clockInMode === 'OUTSTATION' || row.clockOutMode === 'OUTSTATION') outstation += 1;
      else present += 1;
    }
    return { present, late, wfh, outstation };
  }, [mergedRecords]);

  const weeks = useMemo(
    () => (viewTab === 'mine' ? groupByWeek(mergedRecords) : []),
    [mergedRecords, viewTab]
  );

  const shiftMonth = (delta: number) => {
    const d = new Date(year, month - 1 + delta, 1);
    setMonth(d.getMonth() + 1);
    setYear(d.getFullYear());
  };

  const historyTitle = viewTab === 'team' ? 'Team attendance' : 'Monthly history';

  return (
    <div className="space-y-4">
      {canViewTeam && (
        <div className="flex gap-2 rounded-2xl border border-border bg-muted/30 p-1">
          <button
            type="button"
            onClick={() => setViewTab('mine')}
            className={cn(
              'flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-all',
              viewTab === 'mine'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            My history
          </button>
          <button
            type="button"
            onClick={() => setViewTab('team')}
            className={cn(
              'flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-all',
              viewTab === 'team'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Team attendance
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-foreground">{historyTitle}</h2>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => shiftMonth(-1)}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[9rem] text-center text-sm font-medium text-foreground">
            {monthLabel(month, year)}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => shiftMonth(1)}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <LoadingState label="Loading attendance history…" rows={3} />
      ) : (
        <>
          {viewTab === 'mine' && stats && (
            <div className="space-y-3 rounded-2xl border border-border bg-muted/20 p-4">
              <p className="text-2xl font-semibold tabular-nums text-foreground">
                {mergedRecords.length}{' '}
                <span className="text-sm font-normal text-muted-foreground">
                  day{mergedRecords.length === 1 ? '' : 's'} recorded this month
                </span>
              </p>
              {stats.totalDays > 0 && (
                <div className="flex h-2 overflow-hidden rounded-full bg-muted">
                  {breakdown.present > 0 && (
                    <div
                      className="bg-success"
                      style={{ width: `${(breakdown.present / stats.totalDays) * 100}%` }}
                    />
                  )}
                  {breakdown.late > 0 && (
                    <div
                      className="bg-destructive"
                      style={{ width: `${(breakdown.late / stats.totalDays) * 100}%` }}
                    />
                  )}
                  {breakdown.wfh > 0 && (
                    <div
                      className="bg-info"
                      style={{ width: `${(breakdown.wfh / stats.totalDays) * 100}%` }}
                    />
                  )}
                  {breakdown.outstation > 0 && (
                    <div
                      className="bg-plum"
                      style={{ width: `${(breakdown.outstation / stats.totalDays) * 100}%` }}
                    />
                  )}
                </div>
              )}
              <div className="grid grid-cols-4 gap-2">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Present</p>
                  <p className="mt-0.5 text-base font-semibold tabular-nums text-foreground">
                    {breakdown.present}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Late</p>
                  <p className="mt-0.5 text-base font-semibold tabular-nums text-foreground">
                    {breakdown.late}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">WFH</p>
                  <p className="mt-0.5 text-base font-semibold tabular-nums text-foreground">
                    {breakdown.wfh}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Outstation</p>
                  <p className="mt-0.5 text-base font-semibold tabular-nums text-foreground">
                    {breakdown.outstation}
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {formatHours(stats.totalWorkHours)} worked
                {stats.earlyDepartureDays > 0
                  ? ` · ${stats.earlyDepartureDays} undertime day${stats.earlyDepartureDays === 1 ? '' : 's'}`
                  : ''}
              </p>
            </div>
          )}

          <Card>
            <CardContent className="px-4 py-1">
              {mergedRecords.length === 0 ? (
                <EmptyState
                  icon={Clock}
                  title={viewTab === 'team' ? 'No team records this month' : 'No records this month'}
                  description={
                    viewTab === 'team'
                      ? 'Team attendance for this month will appear here.'
                      : 'Clock in from the card above to start building your attendance history.'
                  }
                />
              ) : viewTab === 'mine' ? (
                weeks.map((week) => (
                  <div key={week.key}>
                    <p className="pt-3 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {week.label}
                    </p>
                    {week.rows.map((record) => (
                      <DayRow key={record.date} record={record} />
                    ))}
                  </div>
                ))
              ) : (
                mergedRecords.map((record) => (
                  <DayRow
                    key={`${record.date}-${record.employee?.id ?? 'mine'}`}
                    record={record}
                    showEmployee
                  />
                ))
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
